import { useState } from 'react';
import { Palette, ChevronDown, Check } from 'lucide-react';
import { COLOR_SCHEMES } from '@/constants/timeline';
import { Dropdown } from './Dropdown';
import { Menu } from './Menu';
import { cn } from '@lean-agility/design-system/react';

export interface ColorSchemeSelectorProps {
  /** Currently selected color scheme ID */
  value: string;
  /** Callback when selection changes */
  onChange: (schemeId: string) => void;
  /** Optional additional className */
  className?: string;
}

/**
 * Color scheme selector molecule - dropdown for choosing timeline colors
 *
 * Allows users to select from predefined color schemes for timeline visualization.
 * Uses the design system Dropdown/Menu/MenuItem components for consistent styling.
 */
export function ColorSchemeSelector({ value, onChange, className = '' }: ColorSchemeSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedScheme = COLOR_SCHEMES.find((s) => s.id === value) ?? COLOR_SCHEMES[0];

  const handleSelect = (schemeId: string) => {
    onChange(schemeId);
    setIsOpen(false);
  };

  return (
    <Dropdown
      className={className}
      controlled
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      align="left"
      trigger={
        <button
          type="button"
          className={cn(
            'flex items-center gap-2 text-xs',
            'rounded-md border border-border-strong bg-bg-elevated px-2 py-1',
            `
              text-text-secondary
              hover:text-text-primary
            `,
            'focus:ring-2 focus:ring-primary-500 focus:outline-hidden',
            'transition-colors',
          )}
        >
          <Palette className="size-4 text-text-subtle" />
          <span>{selectedScheme?.name}</span>
          <ChevronDown className={cn('size-3 transition-transform', isOpen && `rotate-180`)} />
        </button>
      }
    >
      <Menu width="sm">
        <div className="py-1">
          {COLOR_SCHEMES.map((scheme) => {
            const isSelected = value === scheme.id;
            return (
              <button
                key={scheme.id}
                type="button"
                onClick={() => handleSelect(scheme.id)}
                className={cn(
                  `flex w-full items-center justify-between gap-2 px-3 py-2 text-sm`,
                  `
                    text-text-primary transition-colors
                    hover:bg-bg-subtle
                  `,
                  isSelected && 'bg-bg-subtle',
                )}
              >
                <span>{scheme.name}</span>
                {isSelected && <Check className="size-4 shrink-0 text-action-primary" />}
              </button>
            );
          })}
        </div>
      </Menu>
    </Dropdown>
  );
}
